import React from 'react';
import { formatValue, getChangeIndicator, trendArrow, trendBadge, trendColor } from '../format';
import { getPeriod } from '../config';

/**
 * One indicator row: current value beside the same value `period` ago.
 * Renders an explicit unavailable state rather than a substitute number.
 */
const IndicatorCard = ({ item, marketData, period, getComparison }) => {
  const currentValue = marketData?.data?.[item.key];
  const previousValue = marketData?.data?.[`${item.key}_previous`];
  const change = getChangeIndicator(currentValue, previousValue);
  const comparison = getComparison(currentValue, item.key);

  return (
    <div className="border-b border-gray-100 pb-4 last:border-b-0">
      <div className="mb-3">
        <h4 className="font-medium text-gray-900 mb-1">{item.label}</h4>
        <p className="text-xs text-gray-600">{item.description}</p>
      </div>

      <div className="grid gap-3 grid-cols-2">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-center">
            <div className="text-xs font-medium text-gray-500 mb-2">CURRENT</div>
            <div className="text-xl font-bold text-gray-900 mb-2">
              {formatValue(currentValue, item.type)}
            </div>
            <div className={`text-xs flex items-center justify-center space-x-1 ${change.color}`}>
              <span>{change.icon}</span>
              <span className="bg-white px-2 py-1 rounded shadow-sm">{change.text}</span>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-3">
          <div className="text-center">
            <div className="text-xs font-medium text-blue-600 mb-2">
              {getPeriod(period).label.toUpperCase()} AGO
            </div>

            {!comparison ? (
              <div className="text-lg text-gray-400">Data unavailable</div>
            ) : comparison.status ? (
              <div className="text-gray-500 text-sm py-2 bg-white rounded px-2">
                <span className="font-medium">
                  {comparison.status === 'no_api_key'
                    ? 'API key needed'
                    : comparison.status === 'no_data'
                      ? 'No data available'
                      : 'Unavailable'}
                </span>
              </div>
            ) : (
              <>
                <div className="text-xl font-bold text-blue-800 mb-2">
                  {formatValue(comparison.historical, item.type)}
                </div>
                <div className={`text-xs flex items-center justify-center space-x-1 ${trendColor(comparison.trend)}`}>
                  <span className="text-sm">{trendArrow(comparison.trend)}</span>
                  <span className="bg-white px-2 py-1 rounded shadow-sm font-medium">
                    {comparison.difference > 0 ? '+' : ''}
                    {comparison.difference.toFixed(2)}
                  </span>
                  {comparison.percentChange !== null && (
                    <span className={`px-2 py-1 rounded text-xs font-bold ${trendBadge(comparison.trend)}`}>
                      {comparison.percentChange > 0 ? '+' : ''}
                      {comparison.percentChange.toFixed(1)}%
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndicatorCard;
